import React, { useMemo } from 'react';

import { useIntl, type MessageDescriptor } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { EditorActions } from '@atlaskit/editor-core';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { Field } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { serializeEditorDocument } from './serialize-editor-document';
import { useEditorPreset } from './useEditorPreset';

type Props = {
	name: string;
	label: MessageDescriptor;
	placeholder: MessageDescriptor;
	value: string;
	onChange: (value: string) => void;
	testId?: string;
	hideLabel?: boolean;
	maxHeight?: number;
	appearance?: 'comment' | 'chromeless';
	isDisabled?: boolean;
};

const styles = cssMap({
	chromelessEditorBox: {
		paddingBlockStart: token('space.075'),
		paddingInline: token('space.100'),
		borderRadius: token('radius.medium'),
		borderColor: token('color.border'),
		borderWidth: token('border.width.selected'),
		borderStyle: 'solid',
		backgroundColor: token('color.background.input'),
		cursor: 'text',
		'&:focus-within': {
			borderColor: token('color.border.focused'),
		},
	},
});

export const EditorField = ({
	name,
	label,
	placeholder,
	value,
	onChange,
	testId,
	hideLabel = false,
	maxHeight = 300,
	appearance = 'comment',
	isDisabled = false,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const fieldLabel = formatMessage(label);
	const { editorApi, preset } = useEditorPreset(formatMessage(placeholder));
	const editorActions = useMemo(() => new EditorActions(), []);
	const composableEditor = (
		<ComposableEditor
			maxHeight={maxHeight}
			shouldFocus={false}
			preset={preset}
			appearance={appearance}
			assistiveLabel={fieldLabel}
			disabled={isDisabled}
			onChange={() => {
				editorApi?.core.actions.requestDocument((doc) => {
					onChange(serializeEditorDocument(doc));
				});
			}}
			onEditorReady={(actions) => {
				if (value) {
					actions.replaceDocument(value);
				}
			}}
		/>
	);
	const editor =
		appearance === 'chromeless' ? (
			<EditorContext editorActions={editorActions}>
				<Box testId={testId} xcss={styles.chromelessEditorBox}>
					{composableEditor}
				</Box>
			</EditorContext>
		) : (
			<EditorContext editorActions={editorActions}>
				<Box testId={testId}>{composableEditor}</Box>
			</EditorContext>
		);

	if (hideLabel) {
		return editor;
	}

	return (
		<Field name={name} label={fieldLabel}>
			{() => editor}
		</Field>
	);
};
